/**
 * Security Constants
 *
 * Centralized security configuration constants for the application.
 * These constants define security policies, timeouts, and sensitive data patterns.
 *
 * @example
 * ```typescript
 * import { SECURITY_CONFIG } from '@app/core/constants';
 *
 * // Use in a service
 * if (loginAttempts > SECURITY_CONFIG.MAX_LOGIN_ATTEMPTS) {
 *   this.lockAccount();
 * }
 * ```
 */

/**
 * Security configuration object
 *
 * Contains all security-related configuration values used throughout the application.
 */
export const SECURITY_CONFIG = {
  /**
   * Session timeout duration in minutes
   * User will be automatically logged out after this period of inactivity
   */
  SESSION_TIMEOUT_MINUTES: 30,

  /**
   * Session warning duration in minutes
   * User will be warned this many minutes before session expires
   */
  SESSION_WARNING_MINUTES: 2,

  /**
   * Local storage key for authentication token
   */
  TOKEN_STORAGE_KEY: 'company_dashboard_token',

  /**
   * Maximum number of failed login attempts before account lockout
   */
  MAX_LOGIN_ATTEMPTS: 5,

  /**
   * Account lockout duration in minutes after max login attempts exceeded
   */
  LOCKOUT_DURATION_MINUTES: 15,

  /**
   * Minimum password length requirement
   */
  PASSWORD_MIN_LENGTH: 8,

  /**
   * List of field names considered sensitive
   * These fields should be excluded from logs and error reports
   */
  SENSITIVE_FIELDS: ['password', 'token', 'secret', 'key', 'apiKey', 'accessToken', 'refreshToken'],

  /**
   * CSRF token header name
   */
  CSRF_HEADER_NAME: 'X-CSRF-TOKEN',

  /**
   * CSRF token cookie name
   */
  CSRF_COOKIE_NAME: 'XSRF-TOKEN',

  /**
   * Security headers to add to requests
   * 
   * Note: X-Content-Type-Options is a response header set by the server,
   * not a request header. It should not be sent in client requests.
   */
  SECURITY_HEADERS: {
    'X-Requested-With': 'XMLHttpRequest'
  }
} as const;

/**
 * HTTP methods that require CSRF protection
 * GET and HEAD are safe methods and don't require CSRF tokens
 */
export const CSRF_PROTECTED_METHODS = ['POST', 'PUT', 'DELETE', 'PATCH'] as const;

/**
 * Type for CSRF protected HTTP methods
 */
export type CsrfProtectedMethod = typeof CSRF_PROTECTED_METHODS[number];

/**
 * Security event types for logging
 */
export enum SecurityEventType {
  LOGIN = 'login',
  LOGOUT = 'logout',
  LOGIN_FAILED = 'login_failed',
  TOKEN_REFRESH = 'token_refresh',
  TOKEN_EXPIRED = 'token_expired',
  SESSION_TIMEOUT = 'session_timeout',
  SUSPICIOUS_ACTIVITY = 'suspicious_activity',
  XSS_ATTEMPT = 'xss_attempt',
  SQL_INJECTION_ATTEMPT = 'sql_injection_attempt',
  CSRF_TOKEN_MISSING = 'csrf_token_missing',
  UNAUTHORIZED_ACCESS = 'unauthorized_access'
}

/**
 * Storage keys used by the application
 * Centralized to avoid key collisions
 */
export const STORAGE_KEYS = {
  TOKEN: SECURITY_CONFIG.TOKEN_STORAGE_KEY,
  LAST_ACTIVITY: 'company_dashboard_last_activity',
  SESSION_START: 'company_dashboard_session_start'
} as const;
